// @ts-check
/**
 * Stage 4 (Sam, 2026-10-28/29): amenity markers that stand above the ground instead of lying on it. priiScene reads
 * pinDraftFromUrl() and swaps its ground discs and roof badges for these; the drafts from rounds 1-3 stay behind dev URL
 * params for comparison.
 *
 * Every shape is a screen-aligned sprite: always upright with its top up, turning with you as you orbit, its tip on the
 * spot it marks, and a constant size on screen (sized from metres-per-pixel at its own depth). That one rule holds in the
 * 3D view, the walking (navigation) view and the 2D top-down view - Google's pins are upright on screen in all three.
 * Colours and glyphs are the map's own (CATS / mapIcons): only the form changes.
 */

const SS = 4; // canvas pixels per screen pixel
/** Camera distance (m) past which zoom=dots draws dots instead of pins: about the venue's opening view. */
export const DOT_RADIUS = 260;

/** The six shapes. w/h: the sprite's size on screen (px); anchor: how far up from its bottom edge the marked spot is. */
export const PIN_SHAPES = {
  1: { key: 'drop', label: 'Teardrop', w: 30, h: 38, anchor: 0 },
  2: { key: 'hover', label: 'Lollipop (hovers on a stem)', w: 30, h: 50, anchor: 3 },
  3: { key: 'bubble', label: 'Bubble with a tail', w: 32, h: 37, anchor: 0 },
  4: { key: 'tile', label: 'Rounded tile with a tail', w: 32, h: 37, anchor: 0 },
  5: { key: 'orb', label: 'Floating orb + shadow', w: 30, h: 44, anchor: 3 },
  6: { key: 'slim', label: 'Slim teardrop, small dot at the spot', w: 24, h: 36, anchor: 2 },
  // round 2 (Sam, 2026-10-29: "mostly round, just a little bit of a teardrop", Google/Apple Maps): a round head with a short
  // white nub at the bottom, the nub's tip on the spot
  7: { key: 'round', label: 'Round, white nub', w: 30, h: 35, anchor: 0 },
  8: { key: 'roundspot', label: 'Round, white nub, dot at the spot', w: 30, h: 39, anchor: 2 },
};

/** Name cards for the selected pin (round 2: the pin grows and a card names it, in the amenity's own colour). */
export const CARD_STYLES = {
  1: 'Color pill above',
  2: 'White card, color glyph',
  3: 'Name under the spot, no card',
  4: 'Dark glass, color edge, two lines',
  5: 'The pin opens into a capsule',
  6: 'Color card, two lines',
};

/** The zoomed-out marks (round 2). */
export const DOT_STYLES = { a: 'Dot', b: 'Mini badge with glyph', c: 'Bead (a tiny pin)' };

/**
 * Which amenities come in first as you zoom (round 2). Rank 1 is what people look for at a show; the rest wait.
 * Each rank: [pin when the camera is within this many metres, dot within this many, else hidden].
 */
export const ESSENTIAL = new Set([
  'water',
  'restroom',
  'food',
  'medical',
  'bar',
  'entrance',
  'exit',
]);
// 2026-10-30: the lesser ones become pins sooner (Sam read mid-zoom dots as pins being blocked); dots only zoomed well out
// Once the map's title shows (titleRadius: 520 m at a venue, 1200 m at a festival) no amenity is drawn at all, not even a
// dot - the old rule (Sam, 2026-10-30, the whole of Portola covered in dots). (Essentials used to stay dots until
// then; since 2026-10-31 they go at 750 m too.)
// Stage 2 (Sam, 2026-10-31, "dots later"): nothing until you are in a way - essentials dot from 750 m, the rest later.
export const RANK_ZOOM = { 1: [320, 750], 2: [300, 550], 3: [230, 420] };
export function pinMode(rank, radius, titleRadius = Infinity) {
  if (radius > titleRadius) return 'off';
  const [pin, dot] = RANK_ZOOM[rank] || RANK_ZOOM[2];
  return radius <= pin ? 'pin' : radius <= dot ? 'dot' : 'off';
}

export const SEL_STYLES = {
  a: 'Grows, white halo',
  b: 'Rises on a taller stem, purple glow; others dim',
  c: 'Opens into a name card',
};

/**
 * The settled look (Sam, 2026-10-29): round pin 7, ranked zoom with plain dots, card 1 in the amenity's own colour. On
 * everywhere, Studio too since 2026-10-31 (Sam: Studio should look like the published map; it had kept the ground discs).
 * In dev, ?pins=1..8 &sel= &card= &fill= &dots= &xray=1 still try the other drafts, and ?pins=0 brings back the discs.
 */
export function pinDraftFromUrl() {
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(import.meta.env.DEV ? window.location.search : '');
  if (q.get('pins') === '0') return null;
  const shape = PIN_SHAPES[q.get('pins')] ? q.get('pins') : '7';
  const sel = SEL_STYLES[q.get('sel')] ? q.get('sel') : 'a';
  const card = CARD_STYLES[q.get('card')] ? q.get('card') : '1';
  const fill = CARD_FILLS[q.get('fill')] ? q.get('fill') : 'raw';
  // zoom=dots (round 1): past DOT_RADIUS every pin is a dot. dots=a|b|c (round 2): the ranked zoom rule, in that dot style
  const dotStyle = DOT_STYLES[q.get('dots')] ? q.get('dots') : 'a';
  // xray=1: pins draw through buildings and hills (round 1). Default: hidden behind them, like anything else in the scene
  return {
    shape,
    sel,
    card,
    fill,
    dots: true,
    ranked: q.get('zoom') !== 'dots',
    dotStyle,
    xray: q.get('xray') === '1',
  };
}

function teardrop(ctx, cx, cy, r, tipY) {
  // a circle of radius r at (cx, cy) whose sides run tangent down to a point at tipY
  const d = tipY - cy;
  const a = Math.asin(Math.min(0.99, r / d));
  ctx.beginPath();
  ctx.arc(cx, cy, r, Math.PI / 2 + a, Math.PI / 2 - a + Math.PI * 2, false);
  ctx.lineTo(cx, tipY);
  ctx.closePath();
}

function nub(ctx, cx, y0, tipY, fill = '#ffffff') {
  // the short white tip under a round head: soft shoulders and a rounded end, no point (Sam, 2026-10-29: "we're not a
  // very sharp and pointy application")
  const r = 1.8;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,.35)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 7, y0 - 4);
  ctx.bezierCurveTo(cx - 4.5, y0 + 1, cx - r - 0.6, tipY - r - 2.5, cx - r, tipY - r);
  ctx.arc(cx, tipY - r, r, Math.PI, 0, true);
  ctx.bezierCurveTo(cx + r + 0.6, tipY - r - 2.5, cx + 4.5, y0 + 1, cx + 7, y0 - 4);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * One pin as a canvas. paint(ctx, cx, cy, size) draws the white glyph. sel: this one is selected (in selection style
 * selStyle); name: the place's name for the callout. Returns { canvas, w, h, anchor } (w/h/anchor in screen px).
 */
export function pinCanvas({
  shape,
  color,
  paint,
  sel = false,
  selStyle = 'a',
  name = '',
  exit = false,
}) {
  const S = PIN_SHAPES[shape];
  let { w, h, anchor } = S;
  const lift = sel && selStyle === 'b' ? 14 : 0; // b: the selected one rises
  const card = sel && selStyle === 'c' && name;
  const cardW = card ? Math.min(150, 26 + name.length * 6.4) : 0;
  const padTop = sel ? 6 : 0; // room for the halo / glow
  const W = Math.max(w + 12, cardW + 12),
    H = h + lift + padTop + 4;
  const c = document.createElement('canvas');
  c.width = Math.ceil(W * SS);
  c.height = Math.ceil(H * SS);
  const ctx = c.getContext('2d');
  ctx.scale(SS, SS);
  const cx = W / 2,
    bottom = H - 2; // the bottom edge of the shape; the spot is `anchor` px above it
  const top = bottom - h - lift;
  const fill = exit ? '#ffffff' : color;
  const edge = exit ? color : '#ffffff';
  const glyph = (x, y, s) => {
    if (exit) {
      ctx.fillStyle = color;
      ctx.font = `900 ${s * 0.62}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('EE', x, y + s * 0.03);
      return;
    }
    paint(ctx, x, y, s);
  };
  const shadow = () => {
    ctx.shadowColor = 'rgba(0,0,0,.45)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1.5;
  };
  const noShadow = () => {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
  };
  const halo = (x, y, r) => {
    if (!sel) return;
    if (selStyle === 'a') {
      ctx.beginPath();
      ctx.arc(x, y, r + 4.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,.95)';
      ctx.fill();
    }
    if (selStyle === 'b') {
      ctx.save();
      ctx.shadowColor = 'rgba(178,102,235,.95)';
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(x, y, r + 1, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(178,102,235,.6)';
      ctx.fill();
      ctx.restore();
    }
  };
  const groundDot = (r) => {
    ctx.beginPath();
    ctx.ellipse(cx, bottom - anchor, r * 1.5, r * 0.6, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(10,6,20,.45)';
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(cx, bottom - anchor, r * 0.8, r * 0.34, 0, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = edge;
    ctx.stroke();
  };
  const stem = (y0, y1) => {
    ctx.beginPath();
    ctx.moveTo(cx, y0);
    ctx.lineTo(cx, y1);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(255,255,255,.9)';
    ctx.stroke();
  };

  const k = S.key;
  if (k === 'drop' || k === 'slim') {
    const r = k === 'slim' ? 10 : 12.5,
      hy = top + r + 1.5;
    if (lift) stem(hy + r, bottom - anchor);
    halo(cx, hy, r);
    if (k === 'slim') groundDot(2.2);
    shadow();
    teardrop(ctx, cx, hy, r, lift ? hy + r + 7 : bottom - anchor);
    ctx.fillStyle = fill;
    ctx.fill();
    noShadow();
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = edge;
    ctx.stroke();
    glyph(cx, hy, r * 1.15);
  } else if (k === 'hover') {
    const r = 12.5,
      hy = top + r + 1.5;
    stem(hy + r - 1, bottom - anchor);
    groundDot(2.6);
    halo(cx, hy, r);
    shadow();
    ctx.beginPath();
    ctx.arc(cx, hy, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    noShadow();
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = edge;
    ctx.stroke();
    glyph(cx, hy, r * 1.15);
  } else if (k === 'bubble' || k === 'tile') {
    const r = 13,
      hy = top + r + 1.5,
      tail = lift ? hy + r + 7 : bottom;
    if (lift) stem(hy + r, bottom);
    halo(cx, hy, r + (k === 'tile' ? 1 : 0));
    shadow();
    if (k === 'bubble') {
      ctx.beginPath();
      ctx.arc(cx, hy, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
    } else {
      roundRect(ctx, cx - r, hy - r, r * 2, r * 2, 7);
      ctx.fillStyle = fill;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.moveTo(cx - 5, hy + r - 1);
    ctx.lineTo(cx, tail);
    ctx.lineTo(cx + 5, hy + r - 1);
    ctx.closePath();
    ctx.fill();
    noShadow();
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = edge;
    if (k === 'bubble') {
      ctx.beginPath();
      ctx.arc(cx, hy, r, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      roundRect(ctx, cx - r, hy - r, r * 2, r * 2, 7);
      ctx.stroke();
    }
    glyph(cx, hy, r * 1.15);
  } else if (k === 'round' || k === 'roundspot') {
    const r = 13,
      hy = top + r + 1.5,
      nubY = k === 'round' ? bottom - anchor : bottom - anchor - 5;
    if (k === 'roundspot') {
      ctx.beginPath();
      ctx.arc(cx, bottom - anchor, 2.2, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#fff';
      ctx.stroke();
    }
    nub(ctx, cx, hy + r, nubY);
    halo(cx, hy, r);
    shadow();
    ctx.beginPath();
    ctx.arc(cx, hy, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    noShadow();
    ctx.lineWidth = 2;
    ctx.strokeStyle = edge;
    ctx.stroke();
    glyph(cx, hy, r * 1.15);
  } else if (k === 'orb') {
    const r = 12.5,
      hy = top + r + 1.5;
    groundDot(2.4);
    if (lift) stem(hy + r, bottom - anchor);
    halo(cx, hy, r);
    shadow();
    ctx.beginPath();
    ctx.arc(cx, hy, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    noShadow();
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = edge;
    ctx.stroke();
    glyph(cx, hy, r * 1.15);
  }
  if (card) {
    // c: the name rides above the pin on a white card with a little pointer
    const cy = top - 2,
      ch = 20,
      x0 = cx - cardW / 2;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.4)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetY = 1.5;
    roundRect(ctx, x0, cy - ch, cardW, ch, 10);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#1b1426';
    ctx.font = '700 11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.length > 22 ? `${name.slice(0, 21)}…` : name, cx, cy - ch / 2 + 0.5);
  }
  // the canvas grew upward for the card: the sprite's size covers it
  const extraTop = card ? 24 : 0;
  if (extraTop) {
    const c2 = document.createElement('canvas');
    c2.width = c.width;
    c2.height = Math.ceil((H + extraTop) * SS);
    c2.getContext('2d').drawImage(c, 0, extraTop * SS);
    // redraw the card into the new top margin (it was clipped above the old canvas)
    const g = c2.getContext('2d');
    g.scale(SS, SS);
    const cy = extraTop + top - 2,
      ch = 20,
      x0 = cx - cardW / 2;
    g.save();
    g.shadowColor = 'rgba(0,0,0,.4)';
    g.shadowBlur = 5;
    g.shadowOffsetY = 1.5;
    roundRect(g, x0, cy - ch, cardW, ch, 10);
    g.fillStyle = '#fff';
    g.fill();
    g.restore();
    g.fillStyle = '#1b1426';
    g.font = '700 11px Inter, system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(name.length > 22 ? `${name.slice(0, 21)}…` : name, cx, cy - ch / 2 + 0.5);
    return { canvas: c2, w: W, h: H + extraTop, anchor: anchor + 2 };
  }
  return { canvas: c, w: W, h: H, anchor: anchor + 2 };
}

/** The zoomed-out mark, its centre (or, for the bead, its tip) on the spot. style: DOT_STYLES key. */
export function dotCanvas(color, style = 'a', paint = null) {
  const w = style === 'b' ? 18 : 14,
    h = style === 'c' ? 18 : w,
    c = document.createElement('canvas');
  c.width = w * SS;
  c.height = h * SS;
  const ctx = c.getContext('2d');
  ctx.scale(SS, SS);
  ctx.shadowColor = 'rgba(0,0,0,.4)';
  ctx.shadowBlur = 2;
  if (style === 'c') {
    nub(ctx, w / 2, 11, h - 1);
    ctx.shadowColor = 'rgba(0,0,0,.4)';
    ctx.shadowBlur = 2;
    ctx.beginPath();
    ctx.arc(w / 2, 6.5, 5.2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
    return { canvas: c, w, h, anchor: 1 };
  }
  const r = style === 'b' ? 7.6 : 4.8;
  ctx.beginPath();
  ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.lineWidth = style === 'b' ? 1.3 : 1.5;
  ctx.strokeStyle = '#fff';
  ctx.stroke();
  if (style === 'b' && paint) paint(ctx, w / 2, h / 2, 9);
  // its bottom on the spot, not its centre: a centred dot's lower half went under the ground in 3D (Sam, 2026-10-30)
  return { canvas: c, w, h, anchor: 1 };
}

const GROW = 1.3; // the selected pin's size against the rest
const FONT = 'Inter, system-ui, -apple-system, sans-serif';
const INK = '#1b1426';
function lum(hex) {
  const n = parseInt(hex.slice(1), 16),
    ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}
function tint(hex, k) {
  // mix toward white by k (0..1)
  const n = parseInt(hex.slice(1), 16);
  const m = (v) => Math.round(v + (255 - v) * k);
  return `rgb(${m((n >> 16) & 255)},${m((n >> 8) & 255)},${m(n & 255)})`;
}
/** The colour darkened (same hue) until white text on it reaches `target` contrast. */
export function deepen(hex, target = 4.5) {
  const n = parseInt(hex.slice(1), 16),
    rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  for (let k = 1; k > 0.2; k -= 0.01) {
    const h =
      '#' +
      rgb
        .map((v) =>
          Math.round(v * k)
            .toString(16)
            .padStart(2, '0'),
        )
        .join('');
    if (1.05 / (lum(h) + 0.05) >= target) return h;
  }
  return '#1b1426';
}
/** Card 1's fill for white text (round 3): raw = the amenity colour, deep = deepened to 4.5:1, edge = deepened to 3:1 with a soft dark edge on the letters. */
export const CARD_FILLS = { raw: 'As is', deep: 'Deepened', edge: 'Light deepen + text edge' };
const clip = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

/**
 * The selected pin (round 2): the pin grown by GROW with a name card in style `card` (CARD_STYLES). kind: the category's
 * word ("Food") for the two-line cards. Returns the same { canvas, w, h, anchor } as pinCanvas.
 */
export function selectedCanvas({
  shape,
  color,
  paint,
  name,
  kind = '',
  card,
  fill = 'raw',
  exit = false,
}) {
  const base = pinCanvas({ shape, color, paint, exit });
  const pw = base.w * GROW,
    ph = base.h * GROW,
    pa = base.anchor * GROW;
  const darkText = lum(color) > 0.45;
  const onColor = darkText ? INK : '#ffffff';
  const meas = document.createElement('canvas').getContext('2d');
  const tw = (font, t) => {
    meas.font = font;
    return meas.measureText(t).width;
  };
  const title = clip(name || kind, 26);
  const F1 = `700 12px ${FONT}`,
    F2 = `600 10px ${FONT}`;

  let cw = 0,
    ch = 0;
  if (card === '1') {
    cw = tw(F1, title) + 22;
    ch = 24;
  }
  if (card === '2') {
    cw = tw(F1, title) + 40;
    ch = 28;
  }
  if (card === '3') {
    cw = tw(`800 12px ${FONT}`, title) + 8;
    ch = 16;
  }
  if (card === '4' || card === '6') {
    cw = Math.max(tw(F1, title), tw(F2, kind)) + (card === '6' ? 44 : 26);
    ch = 36;
  }
  if (card === '5') {
    cw = tw(F1, title) + 46;
    ch = 32;
  }
  cw = Math.min(cw, 190);

  const below = card === '3';
  const capsule = card === '5';
  const nubH = 8;
  const W = Math.ceil(Math.max(pw, cw) + 12);
  const H = Math.ceil(capsule ? ch + nubH + 8 : below ? ph + ch + 6 : ph + ch + 8);
  const c = document.createElement('canvas');
  c.width = W * SS;
  c.height = H * SS;
  const g = c.getContext('2d');
  g.scale(SS, SS);
  const cx = W / 2;
  const shadow = (b = 6) => {
    g.shadowColor = 'rgba(0,0,0,.42)';
    g.shadowBlur = b;
    g.shadowOffsetY = 1.5;
  };
  const flat = () => {
    g.shadowColor = 'transparent';
    g.shadowBlur = 0;
    g.shadowOffsetY = 0;
  };
  const text = (t, x, y, font, fill, align = 'center') => {
    g.font = font;
    g.fillStyle = fill;
    g.textAlign = /** @type {CanvasTextAlign} */ (align);
    g.textBaseline = 'middle';
    g.fillText(t, x, y);
  };
  const glyphDisc = (x, y, r, fill) => {
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fillStyle = fill;
    g.fill();
    paint(g, x, y, r * 1.15);
  };

  if (capsule) {
    // 5: the head widens into a pill with the glyph and the name; the white nub stays under its middle
    const y0 = 3,
      x0 = cx - cw / 2;
    nub(g, cx, y0 + ch, y0 + ch + nubH);
    shadow();
    roundRect(g, x0, y0, cw, ch, ch / 2);
    g.fillStyle = color;
    g.fill();
    flat();
    g.lineWidth = 2;
    g.strokeStyle = '#fff';
    g.stroke();
    paint(g, x0 + 17, y0 + ch / 2, 15);
    text(title, x0 + 32, y0 + ch / 2 + 0.5, F1, onColor, 'left');
    return { canvas: c, w: W, h: H, anchor: 2 };
  }

  const pinTop = below ? 2 : H - ph - 2;
  g.drawImage(base.canvas, cx - pw / 2, pinTop, pw, ph);
  const spotY = pinTop + ph - pa;
  if (below) {
    // 3: Apple's way - no card, the name sits under the spot in the amenity's colour with a dark edge
    g.font = `800 12px ${FONT}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    g.lineWidth = 3.5;
    g.strokeStyle = 'rgba(13,9,24,.92)';
    g.strokeText(title, cx, spotY + 10);
    g.fillStyle = tint(color, 0.35);
    g.fillText(title, cx, spotY + 10);
    return { canvas: c, w: W, h: H, anchor: H - spotY };
  }
  const y0 = 3,
    x0 = cx - cw / 2,
    mid = y0 + ch / 2;
  const pointer = (fill) => {
    g.beginPath();
    g.moveTo(cx - 5, y0 + ch - 0.5);
    g.lineTo(cx, y0 + ch + 4.5);
    g.lineTo(cx + 5, y0 + ch - 0.5);
    g.closePath();
    g.fillStyle = fill;
    g.fill();
  };
  if (card === '1') {
    // white text always (Sam, 2026-10-29); the fill gives way instead, per CARD_FILLS
    const bg = fill === 'raw' ? color : deepen(color, fill === 'edge' ? 3 : 4.5);
    shadow();
    roundRect(g, x0, y0, cw, ch, ch / 2);
    g.fillStyle = bg;
    g.fill();
    flat(); // no pointer: the pin already points down (Sam, 2026-10-29)
    g.lineWidth = 1.5;
    g.strokeStyle = 'rgba(255,255,255,.9)';
    roundRect(g, x0, y0, cw, ch, ch / 2);
    g.stroke();
    if (fill === 'edge') {
      g.font = F1;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.lineJoin = 'round';
      g.lineWidth = 2.4;
      g.strokeStyle = 'rgba(13,9,24,.45)';
      g.strokeText(title, cx, mid + 0.5);
    }
    text(title, cx, mid + 0.5, F1, '#ffffff');
  } else if (card === '2') {
    shadow();
    roundRect(g, x0, y0, cw, ch, 10);
    g.fillStyle = '#ffffff';
    g.fill();
    flat();
    pointer('#ffffff');
    glyphDisc(x0 + 15, mid, 9, color);
    text(title, x0 + 30, mid + 0.5, F1, INK, 'left');
  } else if (card === '4') {
    shadow();
    roundRect(g, x0, y0, cw, ch, 10);
    g.fillStyle = 'rgba(22,12,38,.9)';
    g.fill();
    flat();
    pointer('rgba(22,12,38,.9)');
    g.lineWidth = 1;
    g.strokeStyle = 'rgba(255,255,255,.22)';
    roundRect(g, x0, y0, cw, ch, 10);
    g.stroke();
    g.save();
    roundRect(g, x0, y0, cw, ch, 10);
    g.clip();
    g.fillStyle = color;
    g.fillRect(x0, y0, 4, ch);
    g.restore();
    text(title, x0 + 13, y0 + 12.5, F1, '#ffffff', 'left');
    text(kind, x0 + 13, y0 + 26, F2, tint(color, 0.3), 'left');
  } else if (card === '6') {
    shadow();
    roundRect(g, x0, y0, cw, ch, 12);
    g.fillStyle = color;
    g.fill();
    flat();
    pointer(color);
    g.beginPath();
    g.arc(x0 + 18, mid, 11, 0, Math.PI * 2);
    g.fillStyle = darkText ? 'rgba(27,20,38,.18)' : 'rgba(255,255,255,.22)';
    g.fill();
    paint(g, x0 + 18, mid, 13);
    text(title, x0 + 35, y0 + 12.5, F1, onColor, 'left');
    text(
      kind,
      x0 + 35,
      y0 + 26,
      F2,
      darkText ? 'rgba(27,20,38,.7)' : 'rgba(255,255,255,.8)',
      'left',
    );
  }
  return { canvas: c, w: W, h: H, anchor: H - spotY };
}

/**
 * A person as a pin (Sam, 2026-10-30: friends on the Maps page drawn like the amenity pins, a little bigger): the round
 * head holds their face (paintFace(ctx, cx, cy, r) draws it, clipped to the circle), with a white edge and the same soft
 * white tip. `count` puts a small purple badge on a stack of people. `scale` 1.3 for the one selected or walked to.
 * Returns { canvas, w, h, anchor } like pinCanvas.
 */
export const PERSON_PIN = { k: 1.5, sel: 1.3, edge: 1.7 }; // bigger than an amenity pin; the edge a touch thinner than round 1's (Sam, 2026-10-30)
export function personPinCanvas({
  paintFace,
  count = 0,
  scale = 1,
  head = '#ffffff',
  halo = false,
  glow = false,
  sel = null,
  selColor = head,
  name = '',
  label = '1',
}) {
  // the selected friend's name, like a selected amenity's card (labelStyle in lib/map/faceStyle.js): above the head in a
  // pill (1: their colour, g: dark glass) or under the spot (3)
  const title = clip(
    String(name || '')
      .trim()
      .split(/\s+/)[0] || '',
    16,
  );
  const LF = `700 12px ${FONT}`,
    below = Boolean(title) && label === '3';
  let lw = 0,
    lh = 0;
  if (title) {
    const meas = document.createElement('canvas').getContext('2d');
    meas.font = below ? `800 12px ${FONT}` : LF;
    lw = Math.ceil(meas.measureText(title).width) + (below ? 8 : 22);
    lh = below ? 16 : 24;
  }
  const K = PERSON_PIN.k * scale,
    r = 13 * K,
    nubH = 6 * K,
    pad = sel && sel !== 'a' ? 12 : 4, // room for a selected glow or halo (?psel=, lib/map/faceStyle.js)
    // the count chip sits on the head's right shoulder (Sam's pick G, 2026-11-01): a dark glass pill, lavender edge
    chipH = 12 * K,
    chipW = (String(count).length > 1 ? 17.3 : 13.3) * K,
    chipX = 0.45 * r, // from the head's centre
    // room for the chip on both sides, so the tip stays at the canvas's centre (the sprite's anchor), and above it
    side = count ? Math.max(0, chipX + chipW + 1 - r - pad) : 0,
    chipTop = count ? Math.max(0, Math.ceil(1.33 * K + 1 - pad)) : 0,
    top = title && !below ? Math.max(chipTop, lh + 6) : chipTop,
    under = below ? lh + 4 : 0;
  const W = Math.ceil(Math.max(2 * (r + pad + side), lw + 8)),
    H = Math.ceil(2 * r + nubH + pad * 2 + 2 + top + under);
  const c = document.createElement('canvas');
  c.width = W * SS;
  c.height = H * SS;
  const ctx = c.getContext('2d');
  ctx.scale(SS, SS);
  const cx = W / 2,
    cy = pad + top + r,
    tipY = cy + r + nubH;
  // selected (?psel=b|c|d): a glow in the person's colour (b, d) and/or the selected amenity's white halo (c, d)
  if (sel === 'b' || sel === 'd') {
    ctx.save();
    ctx.shadowColor = selColor;
    ctx.shadowBlur = 14;
    for (let i = 0; i < 2; i++) {
      ctx.beginPath();
      ctx.arc(cx, cy, r + (sel === 'd' ? 3.5 : 1), 0, Math.PI * 2);
      ctx.fillStyle = selColor;
      ctx.fill();
    }
    ctx.restore();
  }
  if (sel === 'c' || sel === 'd') {
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,.95)';
    ctx.fill();
  }
  if (halo) nub(ctx, cx, cy + r, tipY + 1, 'rgba(255,255,255,.9)');
  nub(ctx, cx, cy + r, tipY, head);
  ctx.save();
  ctx.shadowColor = glow ? head : 'rgba(0,0,0,.45)';
  ctx.shadowBlur = glow ? 9 : 4;
  ctx.shadowOffsetY = glow ? 0 : 1.5;
  if (halo) {
    ctx.beginPath();
    ctx.arc(cx, cy, r + 1.2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,.9)';
    ctx.fill();
    ctx.shadowColor = 'transparent';
  }
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = head;
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r - PERSON_PIN.edge * K, 0, Math.PI * 2);
  ctx.clip();
  paintFace(ctx, cx, cy, r - PERSON_PIN.edge * K);
  ctx.restore();
  if (count) {
    const x0 = cx + chipX,
      y0 = cy - r - 1.33 * K;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.45)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetY = 1;
    ctx.beginPath();
    ctx.roundRect(x0, y0, chipW, chipH, chipH / 2);
    ctx.fillStyle = '#1b1230';
    ctx.fill();
    ctx.restore();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#c89bff';
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = `800 ${Math.round(8 * K)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(count), x0 + chipW / 2, y0 + chipH / 2 + 0.5);
  }
  if (title && below) {
    // 3: Apple's way, as the amenity card 3 - the name under the spot in their colour with a dark edge
    ctx.font = `800 12px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3.5;
    ctx.strokeStyle = 'rgba(13,9,24,.92)';
    ctx.strokeText(title, cx, tipY + 4 + lh / 2);
    ctx.fillStyle = tint(selColor, 0.35);
    ctx.fillText(title, cx, tipY + 4 + lh / 2);
  } else if (title) {
    const x0 = cx - lw / 2,
      y0 = 2;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.42)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 1.5;
    roundRect(ctx, x0, y0, lw, lh, lh / 2);
    // 1: the amenity card's pill in their colour, deepened for white text; g: the count chip's dark glass
    ctx.fillStyle = label === 'g' ? '#1b1230' : deepen(selColor, 3);
    ctx.fill();
    ctx.restore();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = label === 'g' ? selColor : 'rgba(255,255,255,.9)';
    ctx.stroke();
    ctx.font = LF;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, cx, y0 + lh / 2 + 0.5);
  }
  return { canvas: c, w: W, h: H, anchor: H - tipY };
}

/**
 * A covered pin's ghost (Sam's pick B, 2026-11-01): the part of a pin a building hides shows through faintly, so a pin is
 * never lost behind something but still reads as behind it. A child sprite with the pin's own picture, drawn over the
 * scene just before the pin; the depth-tested pin then covers it wherever the pin itself is seen. `on` is false for the
 * selected pin (drawn whole, on top) and under ?xray. T: three.js.
 */
export const GHOST_OPACITY = 0.35;
/** @param {any} T @param {any} sp @param {boolean} on */
/**
 * A standing pin's draw order, from how far its spot (not its pulled sprite) is from the camera: nearer draws later, so
 * on top. Pins are pulled toward the camera to clear their own building, by up to half that building's width (priiScene
 * pinPull), so sorting by the sprite let a far building's bar pin pass in front of the near building's food pin (Sam,
 * 2026-11-04, the Bowl). 5 to 5.49: over dots (4) and ghosts (order - 0.5), under friends' selected pins (8-10).
 */
export const pinOrder = (d) => 5 + 0.49 / (1 + d / 50);

export function syncPinGhost(T, sp, on) {
  let g = sp.userData.ghost;
  if (!g) {
    if (!on) return;
    g = new T.Sprite(
      new T.SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false }),
    );
    g.raycast = () => {}; // taps go to the pin itself
    sp.add(g);
    sp.userData.ghost = g;
  }
  g.visible = on;
  if (!on) return;
  if (g.material.map !== sp.material.map) {
    g.material.map = sp.material.map;
    g.material.needsUpdate = true;
  }
  g.center.copy(sp.center);
  g.material.opacity = GHOST_OPACITY * sp.material.opacity;
  g.renderOrder = sp.renderOrder - 0.5;
}

/**
 * You as a pin (Sam's pick D, 2026-11-02: the bubble pin). A glass bubble on a stand, standing on your spot like a
 * friend's pin and following the same rules: hidden behind what is in front of you, ghosting through where covered, head
 * only from straight above. The bubble and the stand are still being picked (dev `?self=<bubble><stand>`, e.g. `?self=1n`,
 * lib/map/faceStyle.js selfLook); until then you are the puck.
 */
export const SELF_BUBBLES = {
  1: 'Plum glass',
  2: 'Lavender glass',
  3: 'Plum, white edge',
  4: 'Plum, lavender glow',
  5: 'Prii mark inside',
  6: 'Pearl',
  7: 'Glass with a lit core',
  8: 'Night glass, lavender rim',
  9: 'Solid purple, white edge',
  10: 'Like a friend (glass + ring)',
};
export const SELF_STANDS = {
  n: 'Short nub',
  w: 'White nub (as friends)',
  t: 'Teardrop',
  d: 'Glass drop',
  s: 'Stem to a dot',
  h: 'Stem + shadow',
  f: 'Floating, shadow below',
  b: 'Floating, light below',
};
const BUBBLE_INK = {
  // [centre, mid, rim, edge line]: the stand takes the rim colour
  plum: ['#3a2879', '#5d3795', '#9d4abd', '#d660ca', '#4b2384'],
  lav: ['#6b3fb0', '#8c5fd0', '#b88cf2', '#e9d6ff', '#6b3fb0'],
  pearl: ['#5b3a9e', '#8f6bd6', '#d7a8f0', '#f6d9ff', '#5b3a9e'],
  night: ['#1b1230', '#231838', '#2e1f4a', '#3a2860', '#c89bff'],
};
function bubbleInk(b) {
  return b === 2
    ? BUBBLE_INK.lav
    : b === 6
      ? BUBBLE_INK.pearl
      : b === 8
        ? BUBBLE_INK.night
        : BUBBLE_INK.plum;
}
/** The colour a bubble's stand (and its ground glow) is drawn in. */
export function selfStandColor(b) {
  if (b === 9) return '#ffffff';
  // lavender glass stands on the brand lavender, the arrow's colour (Sam, 2026-11-02)
  if (b === 2 || b === 8 || b === 10) return '#c89bff';
  return bubbleInk(b)[3];
}
function glassFill(ctx, cx, cy, r, ink) {
  const g = ctx.createRadialGradient(cx - r * 0.1, cy + r * 0.05, r * 0.1, cx, cy, r);
  g.addColorStop(0, ink[0]);
  g.addColorStop(0.55, ink[1]);
  g.addColorStop(0.82, ink[2]);
  g.addColorStop(1, ink[3]);
  return g;
}
/** The bubble itself, radius r at (cx, cy). `path` (optional) traces the outline instead of a circle (the teardrop). */
function paintBubble(ctx, cx, cy, r, b, mark, path) {
  const outline = () => {
    ctx.beginPath();
    if (path) path();
    else ctx.arc(cx, cy, r, 0, Math.PI * 2);
  };
  const ink = bubbleInk(b);
  if (b === 4) {
    ctx.save();
    ctx.shadowColor = '#c89bff';
    ctx.shadowBlur = 14;
    for (let i = 0; i < 3; i++) {
      outline();
      ctx.fillStyle = '#c89bff';
      ctx.fill();
    }
    ctx.restore();
  }
  if (b === 3 || b === 9) {
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.45)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1.5;
    ctx.beginPath();
    if (path) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale((r + 2) / r, (r + 2) / r);
      ctx.translate(-cx, -cy);
      path();
      ctx.restore();
    } else ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();
  }
  outline();
  if (b === 9) ctx.fillStyle = '#9a49d2';
  else if (b === 10) ctx.fillStyle = '#2b1744';
  else ctx.fillStyle = glassFill(ctx, cx, cy, r, ink);
  ctx.fill();
  if (b === 6) {
    // pearl: a pink-to-cyan sheen across the glass
    ctx.save();
    outline();
    ctx.clip();
    const s = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
    s.addColorStop(0, 'rgba(255,170,230,.45)');
    s.addColorStop(0.5, 'rgba(255,255,255,0)');
    s.addColorStop(1, 'rgba(140,220,255,.4)');
    ctx.fillStyle = s;
    ctx.fillRect(cx - r, cy - r, 2 * r, 2 * r);
    ctx.restore();
  }
  if (b === 7) {
    ctx.save();
    ctx.shadowColor = '#f4e6ff';
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.32, 0, Math.PI * 2);
    ctx.fillStyle = '#f7ecff';
    ctx.fill();
    ctx.restore();
  }
  if (b === 5 && mark) {
    const m = r * 1.15;
    ctx.save();
    outline();
    ctx.clip();
    ctx.drawImage(mark, cx - m / 2, cy - m / 2, m, m);
    ctx.restore();
  }
  if (b === 10) {
    ctx.lineWidth = Math.max(2, r * 0.13);
    ctx.strokeStyle = '#c89bff';
    outline();
    ctx.stroke();
  } else if (b !== 9 && b !== 3) {
    ctx.lineWidth = b === 8 ? 1.6 : Math.max(1.2, r * 0.07);
    ctx.strokeStyle = ink[4];
    outline();
    ctx.stroke();
  }
}
function groundShadow(ctx, cx, y, rx, color = 'rgba(8,4,16,.55)') {
  const g = ctx.createRadialGradient(cx, y, 0, cx, y, rx);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(8,4,16,0)');
  ctx.save();
  ctx.translate(cx, y);
  ctx.scale(1, 0.38);
  ctx.translate(-cx, -y);
  ctx.beginPath();
  ctx.arc(cx, y, rx, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.restore();
}
/**
 * You as a bubble pin: { canvas, w, h, anchor } like personPinCanvas, the anchor on your spot. `flat` (looking straight
 * down): the bubble alone, centred on the spot. `mark`: the Prii mark image, for bubble 5 (drawn without it until it loads).
 */
export function selfPinCanvas({
  bubble = 1,
  stand = 'n',
  flat = false,
  scale = 1,
  mark = null,
  glow = null,
}) {
  const K = PERSON_PIN.k * scale,
    r = 13 * K,
    pad = 14, // room for bubble 4's glow
    standH = flat ? 0 : { n: 6, w: 6, t: 9, d: 11, s: 15, h: 15, f: 11, b: 13 }[stand] * K,
    foot = flat ? 0 : { s: 3, h: 4, f: 4, b: 5 }[stand] * K || 0;
  const W = Math.ceil(2 * (r + pad)),
    H = Math.ceil(pad + 2 * r + standH + foot + pad);
  const c = document.createElement('canvas');
  c.width = W * SS;
  c.height = H * SS;
  const ctx = c.getContext('2d');
  ctx.scale(SS, SS);
  const cx = W / 2,
    cy = pad + r,
    spotY = flat ? cy : cy + r + standH,
    col = selfStandColor(bubble);
  if (glow != null) {
    // `glow` (a phase, 0-1): only the light inside the bubble, the same size and anchor as the pin, drawn over it. It
    // swells from the centre toward the rim and back once a cycle (Sam, 2026-11-02: an inner glow, not a halo)
    const b = 0.5 - 0.5 * Math.cos(2 * Math.PI * glow),
      R = r * (0.4 + 0.5 * b);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    g.addColorStop(0, `rgba(240,226,255,${0.3 + 0.55 * b})`);
    g.addColorStop(0.45, `rgba(200,155,255,${0.25 + 0.4 * b})`);
    g.addColorStop(1, 'rgba(200,155,255,0)');
    ctx.beginPath();
    ctx.arc(cx, cy, Math.min(R, r - 1), 0, Math.PI * 2);
    ctx.fillStyle = g;
    ctx.fill();
    return { canvas: c, w: W, h: H, anchor: H - spotY, cy };
  }
  if (flat) {
    paintBubble(ctx, cx, cy, r, bubble, mark);
    return { canvas: c, w: W, h: H, anchor: H - cy, cy };
  }
  let path = null;
  if (stand === 'n' || stand === 'w') nub(ctx, cx, cy + r, spotY, stand === 'w' ? '#ffffff' : col);
  else if (stand === 't' || stand === 'd') {
    // one outline: the head's sides run down to a rounded point on the spot. The drop's tail is longer and softer.
    const tip = 1.4;
    path = () => {
      if (stand === 'd') {
        // the drop: the head runs into a slender tail with curved, pinched sides
        const a0 = 0.95,
          sx = cx + r * Math.cos(Math.PI / 2 + a0),
          sy = cy + r * Math.sin(Math.PI / 2 + a0);
        ctx.arc(cx, cy, r, Math.PI / 2 + a0, Math.PI / 2 - a0 + Math.PI * 2, false);
        ctx.bezierCurveTo(cx + r * 0.3, cy + r * 1.25, cx + 1.4, spotY - 4, cx, spotY);
        ctx.bezierCurveTo(cx - 1.4, spotY - 4, cx - r * 0.3, cy + r * 1.25, sx, sy);
        ctx.closePath();
        return;
      }
      const d = spotY - tip - cy,
        a = Math.asin(Math.min(0.99, r / d));
      ctx.arc(cx, cy, r, Math.PI / 2 + a, Math.PI / 2 - a + Math.PI * 2, false);
      const s = tip * 0.8;
      ctx.lineTo(cx + s, spotY - tip);
      ctx.quadraticCurveTo(cx, spotY + tip * 0.3, cx - s, spotY - tip);
      ctx.closePath();
    };
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.4)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1.5;
    ctx.beginPath();
    path();
    ctx.fillStyle = 'rgba(0,0,0,.01)';
    ctx.fill();
    ctx.restore();
  } else if (stand === 's' || stand === 'h') {
    if (stand === 'h') groundShadow(ctx, cx, spotY, 9 * K);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(20,10,36,.55)';
    ctx.lineWidth = 3.4;
    ctx.beginPath();
    ctx.moveTo(cx, cy + r - 1);
    ctx.lineTo(cx, spotY);
    ctx.stroke();
    ctx.strokeStyle = col;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, spotY, 2.6, 0, Math.PI * 2);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(20,10,36,.7)';
    ctx.stroke();
    ctx.restore();
  } else if (stand === 'f') {
    groundShadow(ctx, cx, spotY, r * 0.8);
  } else if (stand === 'b') {
    const g = ctx.createLinearGradient(0, cy + r * 0.4, 0, spotY);
    g.addColorStop(0, 'rgba(200,155,255,.55)');
    g.addColorStop(1, 'rgba(200,155,255,.08)');
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.55, cy + r * 0.6);
    ctx.lineTo(cx - r * 0.75, spotY);
    ctx.lineTo(cx + r * 0.75, spotY);
    ctx.lineTo(cx + r * 0.55, cy + r * 0.6);
    ctx.closePath();
    ctx.fillStyle = g;
    ctx.fill();
    groundShadow(ctx, cx, spotY, r * 0.9, 'rgba(200,155,255,.6)');
  }
  if (!path) {
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.45)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,.01)';
    ctx.fill();
    ctx.restore();
  }
  paintBubble(ctx, cx, cy, r, bubble, mark, path);
  return { canvas: c, w: W, h: H, anchor: H - spotY, cy };
}
